import { Avatar } from '@creditornot/cb-avatar';
import { Button } from '@creditornot/cb-button';
import { MagnifyingGlass } from '@creditornot/cb-icons';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { Input, InputAlert } from '@creditornot/cb-input';
import { ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { Empty } from '@creditornot/cb-placeholders';
import { InputRadio } from '@creditornot/cb-radio';
import { Tag } from '@creditornot/cb-tag';
import { useMutation } from '@tanstack/react-query';
import { sortBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification, WoltModal } from 'common/components';
import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';
import { startSelectionBootstrapping } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';
import { useVenueList } from 'modules/shared-menu/hooks/useVenueList';
import { VenueWithSharedMenuLinkInfo } from 'modules/shared-menu/hooks/useVenuesWithSelectionLinks';
import { trackSelectionBootstrap } from 'modules/shared-menu/telemetry';
import { SharedMenu } from 'modules/shared-menu/types';

import { useSelectionData } from '../../../hooks/useSelectionData';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  venuesWithLinks: VenueWithSharedMenuLinkInfo[];
};

const Root = styled(WoltModal)`
  width: 640px;
  max-height: 80vh;
  border-radius: 8px;
  background-color: ${cssThemedValue('surfaceMain')};
  display: flex;
  flex-direction: column;
`;

const NoteContainer = styled.div`
  padding: 16px 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const VenueList = styled.div`
  background: ${cssThemedValue('surfaceMain')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  height: 60vh;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const SearchContainer = styled.div`
  padding: 16px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const SearchInput = styled(Input)`
  width: 100%;
`;

const VenueItem = styled.div<{ selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  flex-shrink: 0;
  background-color: ${({ selected }) => (selected ? colors.wolt4 : cssThemedValue('surfaceMain'))};
  transition: background-color 100ms;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  & + & {
    border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  }
  &:hover {
    background-color: ${({ selected }) =>
      selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const EmptyMessage = styled(Empty)`
  width: 100%;
  padding: 48px 16px;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
`;
const Right = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const ImageContainer = styled.div`
  margin: 0 12px;
  border-radius: 50%;

  img {
    object-fit: cover;
  }
`;

const NameAndAddress = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

export const getVenuesNotLinkedToCurrentSharedMenu = (
  sharedMenu: SharedMenu,
  venuesWithLinks: VenueWithSharedMenuLinkInfo[],
) => {
  return venuesWithLinks.filter(
    venue =>
      venue.sharedMenuLink.scope !== sharedMenu.scope.type || venue.sharedMenuLink.sharedMenuId !== sharedMenu.id,
  );
};

export const MenuBootstrapModal = ({ isOpen, onClose, venuesWithLinks }: Props) => {
  const scope = useScopeContext();
  const { selection, updateSelectionData } = useSelectionData();
  const { updateSharedMenuStatus } = useSharedMenuData({ scope });

  const intl = useIntl();

  const [selectedVenueId, setSelectedVenueId] = useState<string | undefined>();

  const venuesNotLinkedToCurrentSharedMenu = useMemo(
    () => getVenuesNotLinkedToCurrentSharedMenu(selection, venuesWithLinks),
    [selection, venuesWithLinks],
  );

  const { searchInput, searchVenues, shownVenues } = useVenueList({
    venues: venuesNotLinkedToCurrentSharedMenu,
  });

  // Cleanup selected venue id if the venue is filtered out of the list
  useEffect(() => {
    if (!(shownVenues ?? []).find(venue => venue.id === selectedVenueId)) {
      setSelectedVenueId(undefined);
    }
  }, [selectedVenueId, shownVenues]);

  const { mutate, isLoading, error } = useMutation(
    () => {
      if (!selectedVenueId) {
        throw new Error('No venue selected for bootstrapping, this should not happen!');
      }
      trackSelectionBootstrap(selectedVenueId);
      return startSelectionBootstrapping(selection.id, selectedVenueId);
    },
    {
      onSuccess: data => {
        updateSelectionData({ actionType: 'updateSelection', payload: data });
        updateSharedMenuStatus(data.id, data.draft);
        onClose();
      },
    },
  );

  const alphabeticallySortedVisibleVenuesWithMenus = useMemo(
    () => sortBy(shownVenues ?? [], venue => localize(venue.name, intl.locale)).filter(venue => !!venue.menu),
    [intl.locale, shownVenues],
  );

  return (
    <Root isOpen={isOpen} onRequestClose={onClose}>
      <ModalHeader onCloseClick={onClose}>
        <T.Heading6>
          <FormattedMessage id="shared-menu.editor.empty-menu.bootstrap.button-label" />
        </T.Heading6>
      </ModalHeader>
      <NoteContainer>
        <InputAlert variant="warning">
          <FormattedMessage id="listing-manager.selections.editor.bootstrap.wolt-specials-note" />
        </InputAlert>
      </NoteContainer>
      <SearchContainer>
        <SearchInput
          onClearClick={() => searchVenues('')}
          value={searchInput}
          icon={<MagnifyingGlass />}
          placeholder={intl.formatMessage({ id: 'shared-menu.toolbar.search-placeholder' })}
          onChange={e => searchVenues(e.target.value)}
        />
      </SearchContainer>
      <VenueList>
        {(!shownVenues || shownVenues.length === 0) && (
          <EmptyMessage title={intl.formatMessage({ id: 'menu.menu-sync.empty-venue-list' })} />
        )}
        {alphabeticallySortedVisibleVenuesWithMenus.length > 0 &&
          alphabeticallySortedVisibleVenuesWithMenus.map(venue => (
            <VenueItem
              key={venue.id}
              selected={selectedVenueId === venue.id}
              onClick={() => setSelectedVenueId(venue.id)}
            >
              <Left>
                <InputRadio readOnly checked={selectedVenueId === venue.id} />
                <ImageContainer>
                  <Avatar size="tiny" imgSrc={venue.listimage ?? undefined} />
                </ImageContainer>
                <NameAndAddress>
                  <T.Body3>{localize(venue.name, intl.locale)}</T.Body3>
                  <T.Small>{venue.address}</T.Small>
                </NameAndAddress>
              </Left>
              {venue.sharedMenuLink.scope === selection.scope.type && (
                <Right>
                  <Tag size="small" variant="lightBlack">
                    {venue.sharedMenuLink.sharedMenuName}
                  </Tag>
                </Right>
              )}
              {venue.sharedMenuLink.scope !== selection.scope.type && venue.sharedMenuLink.scope !== 'none' && (
                <Right>
                  <Tag size="small" variant="lightBlack">
                    <FormattedMessage id="shared-menu.venue-list.other-scope" />
                  </Tag>
                </Right>
              )}
            </VenueItem>
          ))}
      </VenueList>
      <StyledModalFooter>
        {error && <APIErrorNotification error={error} style={{ width: '100%', marginBlockEnd: '16px' }} />}
        <FooterButtons>
          <Button variant="lightBlue" size="small" onClick={onClose} style={{ marginInlineEnd: '8px' }}>
            <FormattedMessage id="state.cancel" />
          </Button>
          <Button disabled={!selectedVenueId} variant="blue" size="small" onClick={() => mutate()} loading={isLoading}>
            <FormattedMessage id="shared-menu.editor.empty-menu.bootstrap.submit" />
          </Button>
        </FooterButtons>
      </StyledModalFooter>
    </Root>
  );
};
